import React, { useEffect, useState, useCallback } from "react";
import {
  X,
  Percent,
  IndianRupee,
  ChevronDown,
  ChevronUp,
  Check,
  Save,
  Loader2,
  AlertCircle,
  Tag,
  FlaskConical,
  ToggleLeft,
  ToggleRight,
  Search,
  Info,
} from "lucide-react";
import axiosInstance from "../../../utils/axiosInstance";

// ─── helpers ────────────────────────────────────────────────
const fmtPrice = (price) =>
  price != null ? `₹${Number(price).toLocaleString("en-IN")}` : "—";

// ─── CenterCommissionsModal ──────────────────────────────────
export default function CenterCommissionsModal({ isOpen, onClose, center }) {
  // categories: [{ commissionId, categoryId, categoryName, isActive, allTests, testMap }]
  // testMap: { [testId]: { type, value } }
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  const [expanded, setExpanded]     = useState({}); // categoryId → bool
  const [searchMap, setSearchMap]   = useState({}); // categoryId → string

  // ── Fetch ────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!center?.id) return;
    setLoading(true);
    setError("");
    try {
      const res  = await axiosInstance.get(`/api/centers/${center.id}/commissions`);
      const data = res.data?.data || [];

      // Build per-category state
      const built = data.map((row) => {
        // Build a map of testId → {type, value} from already-saved commissionTests
        const savedMap = {};
        for (const ct of (row.commissionTests || [])) {
          savedMap[ct.testId] = {
            type:  ct.type  || "PERCENT",
            value: String(ct.value ?? ""),
          };
        }

        return {
          commissionId: row.id,
          categoryId:   row.categoryId,
          categoryName: row.category?.name || "Unknown",
          isActive:     row.isActive !== false,
          allTests:     row.category?.tests || [],
          testMap:      savedMap,
        };
      });

      setCategories(built);
      // Auto-expand categories that already have test commissions
      const initExpand = {};
      built.forEach((c) => {
        if (Object.keys(c.testMap).length > 0) initExpand[c.categoryId] = true;
      });
      setExpanded(initExpand);
    } catch (e) {
      setError("Failed to load commissions");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [center?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setSearchMap({});
      setSuccess("");
      setError("");
    }
  }, [isOpen, fetchData]);

  // ── Helpers ──────────────────────────────────────────────
  const toggleCategory = (categoryId) =>
    setExpanded((p) => ({ ...p, [categoryId]: !p[categoryId] }));

  const setCategoryActive = (categoryId, val) =>
    setCategories((prev) =>
      prev.map((c) => c.categoryId === categoryId ? { ...c, isActive: val } : c)
    );

  const setTestField = (categoryId, testId, field, value) =>
    setCategories((prev) =>
      prev.map((c) => {
        if (c.categoryId !== categoryId) return c;
        const next = { ...c.testMap };
        next[testId] = { ...(next[testId] || { type: "PERCENT", value: "" }), [field]: value };
        return { ...c, testMap: next };
      })
    );

  const clearTestCommission = (categoryId, testId) =>
    setCategories((prev) =>
      prev.map((c) => {
        if (c.categoryId !== categoryId) return c;
        const next = { ...c.testMap };
        delete next[testId];
        return { ...c, testMap: next };
      })
    );

  // bulk-set all visible tests to same type
  const setAllType = (categoryId, type) =>
    setCategories((prev) =>
      prev.map((c) => {
        if (c.categoryId !== categoryId) return c;
        const next = { ...c.testMap };
        c.allTests.forEach((t) => {
          next[t.id] = { ...(next[t.id] || { value: "" }), type };
        });
        return { ...c, testMap: next };
      })
    );

  const clearAll = (categoryId) =>
    setCategories((prev) =>
      prev.map((c) => c.categoryId === categoryId ? { ...c, testMap: {} } : c)
    );

  // ── Save ────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const commissions = categories.map((cat) => ({
        categoryId: cat.categoryId,
        isActive:   cat.isActive,
        tests: Object.entries(cat.testMap)
          .filter(([, v]) => v.value !== "" && v.value !== undefined)
          .map(([testId, v]) => ({
            testId: Number(testId),
            type:   v.type || "PERCENT",
            value:  parseFloat(v.value) || 0,
          })),
      }));

      await axiosInstance.put(`/api/centers/${center.id}/commissions`, { commissions });
      setSuccess("Commissions saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
      await fetchData();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save commissions");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // total saved test count
  const totalConfigured = categories.reduce(
    (sum, c) => sum + Object.keys(c.testMap).length,
    0
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Test-wise Commissions</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {center?.name} — set individual commission per test
              {totalConfigured > 0 && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded-full">
                  {totalConfigured} configured
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">

          {/* Banners */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700">
              <Check className="w-4 h-4 flex-shrink-0" /> {success}
            </div>
          )}

          {/* Hint */}
          {!loading && categories.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Expand a category, then enter the commission amount or % for each test individually.
              Tests with no value entered will have no commission.
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <span className="text-xs">Loading…</span>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Tag className="w-8 h-8 mb-2" />
              <span className="text-sm font-medium text-gray-600">No categories assigned</span>
              <span className="text-xs text-gray-400 mt-1">Assign categories to this center first.</span>
            </div>
          ) : (
            categories.map((cat) => {
              const isExp     = !!expanded[cat.categoryId];
              const search    = searchMap[cat.categoryId] || "";
              const filtered  = cat.allTests.filter((t) =>
                t.name.toLowerCase().includes(search.toLowerCase())
              );
              const configured = Object.keys(cat.testMap).length;

              return (
                <div
                  key={cat.categoryId}
                  className={`border rounded-lg overflow-hidden ${
                    cat.isActive ? "border-blue-200" : "border-gray-200"
                  }`}
                >
                  {/* Category header bar */}
                  <div
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer select-none ${
                      cat.isActive ? "bg-blue-50/40" : "bg-gray-50"
                    }`}
                    onClick={() => toggleCategory(cat.categoryId)}
                  >
                    {/* Icon + name */}
                    <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Tag className="w-3.5 h-3.5 text-blue-600" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900">{cat.categoryName}</p>
                      <p className="text-[10px] text-gray-500">
                        {cat.allTests.length} tests available
                        {configured > 0 && (
                          <span className="ml-1.5 text-blue-600 font-medium">
                            · {configured} with commission
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Active toggle */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setCategoryActive(cat.categoryId, !cat.isActive); }}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border transition ${
                        cat.isActive
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-gray-50 text-gray-500 border-gray-200"
                      }`}
                    >
                      {cat.isActive
                        ? <><ToggleRight className="w-3.5 h-3.5" /> Active</>
                        : <><ToggleLeft  className="w-3.5 h-3.5" /> Off</>}
                    </button>

                    {/* Expand icon */}
                    <div className="text-gray-400">
                      {isExp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>

                  {/* ── Tests panel ─────────────────────────── */}
                  {isExp && (
                    <div className="border-t border-gray-200 bg-white px-4 py-3">

                      {/* Toolbar */}
                      <div className="flex items-center justify-between mb-2.5 gap-3">
                        {/* Search */}
                        <div className="relative flex-1 max-w-xs">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search tests…"
                            value={search}
                            onChange={(e) =>
                              setSearchMap((p) => ({ ...p, [cat.categoryId]: e.target.value }))
                            }
                            className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          />
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Bulk type */}
                          <span className="text-[10px] text-gray-500">Set all:</span>
                          <button
                            onClick={() => setAllType(cat.categoryId, "PERCENT")}
                            className="px-2 py-1 text-[10px] font-medium rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition"
                          >
                            % for all
                          </button>
                          <button
                            onClick={() => setAllType(cat.categoryId, "AMOUNT")}
                            className="px-2 py-1 text-[10px] font-medium rounded border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 transition"
                          >
                            ₹ for all
                          </button>
                          <button
                            onClick={() => clearAll(cat.categoryId)}
                            className="px-2 py-1 text-[10px] font-medium rounded border border-gray-200 text-gray-500 hover:bg-gray-100 transition"
                          >
                            Clear all
                          </button>
                        </div>
                      </div>

                      {/* Column headers */}
                      <div className="grid grid-cols-12 gap-2 px-2 mb-1">
                        <div className="col-span-5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Test</div>
                        <div className="col-span-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Price</div>
                        <div className="col-span-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Type</div>
                        <div className="col-span-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Commission</div>
                      </div>

                      {/* Test rows */}
                      <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
                        {filtered.length === 0 ? (
                          <p className="text-xs text-gray-400 py-4 text-center">No tests found</p>
                        ) : (
                          filtered.map((test) => {
                            const entry   = cat.testMap[test.id];
                            const hasVal  = entry?.value !== "" && entry?.value !== undefined;
                            const type    = entry?.type || "PERCENT";

                            return (
                              <div
                                key={test.id}
                                className={`grid grid-cols-12 gap-2 items-center px-2 py-2 rounded-lg transition ${
                                  hasVal
                                    ? "bg-blue-50 border border-blue-100"
                                    : "bg-gray-50 border border-gray-100 hover:border-gray-200"
                                }`}
                              >
                                {/* Test name */}
                                <div className="col-span-5 min-w-0">
                                  <p className="text-xs font-medium text-gray-800 truncate">{test.name}</p>
                                  <p className="text-[10px] text-gray-400">{test.testType}</p>
                                </div>

                                {/* Offer price */}
                                <div className="col-span-3">
                                  <span className="text-xs text-gray-600">{fmtPrice(test.offerPrice)}</span>
                                </div>

                                {/* Type toggle */}
                                <div className="col-span-2">
                                  <div className="flex items-center bg-white border border-gray-200 rounded p-0.5 w-fit">
                                    <button
                                      onClick={() => setTestField(cat.categoryId, test.id, "type", "PERCENT")}
                                      className={`flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold transition ${
                                        type === "PERCENT"
                                          ? "bg-blue-600 text-white"
                                          : "text-gray-400 hover:text-gray-600"
                                      }`}
                                      title="Percent"
                                    >
                                      <Percent className="w-2.5 h-2.5" />
                                    </button>
                                    <button
                                      onClick={() => setTestField(cat.categoryId, test.id, "type", "AMOUNT")}
                                      className={`flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold transition ${
                                        type === "AMOUNT"
                                          ? "bg-purple-600 text-white"
                                          : "text-gray-400 hover:text-gray-600"
                                      }`}
                                      title="Fixed amount"
                                    >
                                      <IndianRupee className="w-2.5 h-2.5" />
                                    </button>
                                  </div>
                                </div>

                                {/* Value input */}
                                <div className="col-span-2">
                                  <div className="relative">
                                    <input
                                      type="number"
                                      min="0"
                                      max={type === "PERCENT" ? 100 : undefined}
                                      step="0.01"
                                      placeholder="0"
                                      value={entry?.value ?? ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "") {
                                          clearTestCommission(cat.categoryId, test.id);
                                        } else {
                                          setTestField(cat.categoryId, test.id, "value", val);
                                        }
                                      }}
                                      className={`w-full pl-2 pr-5 py-1.5 text-xs border rounded focus:ring-1 focus:outline-none text-right ${
                                        hasVal
                                          ? "border-blue-300 focus:ring-blue-400 bg-white"
                                          : "border-gray-200 focus:ring-blue-400 bg-white"
                                      }`}
                                    />
                                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-gray-400 pointer-events-none">
                                      {type === "PERCENT" ? "%" : "₹"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Summary bar */}
                      <div className="mt-2.5 flex items-center justify-between">
                        <p className="text-[10px] text-gray-500">
                          {configured === 0
                            ? "No commissions set for this category"
                            : `${configured} of ${cat.allTests.length} tests have commissions`}
                        </p>
                        {configured > 0 && (
                          <button
                            onClick={() => clearAll(cat.categoryId)}
                            className="text-[10px] text-red-500 hover:underline"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3 bg-gray-50/80">
          <p className="text-[10px] text-gray-500">
            {totalConfigured > 0
              ? `${totalConfigured} test commission${totalConfigured > 1 ? "s" : ""} configured`
              : "No commissions configured yet"}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || categories.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                : <><Save className="w-3.5 h-3.5" /> Save Commissions</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}